// apps/web/src/features/commercial-proposals/components/CommercialProposalMobileCard.tsx
//
// @aiPurpose mobile (`<900px`) card for the commercial-proposals list
//   (#1198), mirroring the requests-card rhythm: a code + status header, the
//   client/product body, and an updated-time footer. Click / Enter / Space
//   open the detail drawer.
import { useTranslation } from 'react-i18next';
import { CommercialProposalStatus, type CommercialProposalListItem } from '@magimp/shared';

import { StatusBadge } from '../../../ui/index.js';
import { formatDateTime } from '../../../i18n/index.js';
import type { AppLocale } from '../../../i18n/index.js';

function statusBadgeStatus(status: CommercialProposalStatus): 'draft' | 'active' {
  return status === CommercialProposalStatus.PUBLISHED ? 'active' : 'draft';
}

export function CommercialProposalMobileCard({
  row,
  language,
  showOwner,
  onSelect,
}: {
  row: CommercialProposalListItem;
  language: AppLocale;
  showOwner: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation('commercialProposals');
  return (
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    <article
      aria-label={row.publicCode}
      tabIndex={0}
      data-testid={`commercial-proposals-card-${row.id}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
        cursor: 'pointer',
        outline: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--fg-3)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        <span>{row.publicCode}</span>
        <span style={{ flex: 1 }} />
        <StatusBadge status={statusBadgeStatus(row.status)} size="sm" />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>{row.productName}</div>
      <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>{row.client || '—'}</div>
      {showOwner && (
        <div
          data-testid={`commercial-proposals-card-owner-${row.id}`}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            fontSize: 11,
            color: 'var(--fg-3)',
          }}
        >
          <span>{t('columns.owner')}</span>
          <span>{row.ownerName}</span>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
          fontSize: 11,
          color: 'var(--fg-3)',
          borderTop: '1px solid var(--border-2)',
          paddingTop: 6,
        }}
      >
        <span>{t('columns.htmlViews')}</span>
        <span
          data-testid={`commercial-proposals-card-views-${row.id}`}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {row.htmlViewCount}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
          fontSize: 11,
          color: 'var(--fg-3)',
        }}
      >
        <span>{t('columns.updatedAt')}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatDateTime(row.updatedAt, language)}
        </span>
      </div>
    </article>
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  );
}
